import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import Form from "../../../../components/forms/Form";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import { addressFields } from "../../../../constants/forms/addressFields";
import useForm from "../../../../hooks/useForm";
import { maskCep, removeMask } from "../../../../utils/maskUtils";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import SectionHeader from "../../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const CreateSupplierAddressPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { post: baseServicePost, formErrors, setFormErrors } = useBaseService(navigate);
  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues(addressFields)
  );

  const queryClient = useQueryClient();

const fetchCepData = useCallback(
    async (value) => {
      if (value && removeMask(value).length === 8) {
        showLoader();
        try {
          const response = await fetch(
            `https://viacep.com.br/ws/${removeMask(value)}/json/`
          );
          if (!response.ok) throw new Error("Erro ao buscar o CEP");

          const data = await response.json();
          if (data.erro) throw new Error("CEP não encontrado");

          setFormData((prev) => ({
            address: {
              ...prev.address,
              street: data.logradouro || "",
              district: data.bairro || "",
              city: data.localidade || "",
              state: data.estado || "",
              country: "Brasil",
            },
          }));
          showNotification("success", "Endereço preenchido automaticamente!");
        } catch (error) {
          showNotification("error", error.message);
        } finally {
          hideLoader();
        }
      }
    },
    [hideLoader, setFormData, showLoader, showNotification]
  );

  useEffect(() => {
    if (formData.address.zip && removeMask(formData.address.zip).length === 8) {
      fetchCepData(formData.address.zip);
    }
  }, [formData.address.zip]);

  const handleFieldChange = useCallback(
    (fieldId, value) => {
        handleChange(fieldId, value);
    },
    [handleChange]
  );

  const createAddressMutation = useMutation({
      mutationFn: async (data) => {
        const response = await baseServicePost(
          entities.suppliers.addresses.create(id),
          data
        );
        return response;
      },
      onSuccess: (response) => {
        if (response?.status === 201) {
          navigate(`/almoxarifado/fornecedores/detalhes/${id}`);
          queryClient.invalidateQueries({ queryKey: ["almox_supplierAddresses"] });
        }
      },
      onError: (err) => {
        console.error("onError ", err);
        if (err.response?.data?.errors) {
          setFormErrors(err.response.data.errors);
        }
        hideLoader();
      },
      onSettled: () => {
        hideLoader();
      },
    });

  const handleSubmit = async () => {
      try {
        const cleanedData = cleanPayload(formData);
        const sanitizedData = {
          address: {
            ...cleanedData.address,
            addressable_type: "supplier",
            number: cleanedData.address.number ? String(cleanedData.address.number) : null,
            zip: cleanedData.address.zip ? removeMask(cleanedData.address.zip) : "",
          },
        };
  
        return await createAddressMutation.mutateAsync(sanitizedData);
      } catch (error) {
        console.error("Erro ao cadastrar endereço:", {
          status: error.status,
          message: error.message,
        });
      }
    };


  const handleBack = useCallback(() => {
    navigate(`/almoxarifado/fornecedores/detalhes/${id}`);
  }, [navigate, id]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Endereço do Fornecedor"
        showBackButton={true}
        backUrl={`/almoxarifado/fornecedores/detalhes/${id}/`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}>
          {() => {
            const renderedSections = new Set();

            return addressFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleFieldChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateSupplierAddressPage;
